"use client"

import React, { createContext, useContext, useState, useEffect } from "react"
import { useRouter } from "@/lib/compat/navigation"
import { resolveCandidateNext, resolveLoginDestination, type LoginAppAssignment, type LoginOptions } from "@/lib/access-control"

export interface User {
  id: string
  email: string
  full_name: string | null
  role: string
  user_type: string
  created_at: string
  updated_at: string
}

// Auth tokens live in httpOnly cookies set by the backend Worker and are never
// exposed to JS. The client only tracks non-sensitive session metadata.
export interface AuthSession {
  expires_at: number
  user: {
    id: string
    email: string
  }
  profile: User
  platform_role: 'owner' | 'super_admin' | null
}

interface AuthContextType {
  session: AuthSession | null
  isLoading: boolean
  login: (email: string, password: string, options?: LoginOptions) => Promise<void>
  signup: (email: string, password: string, fullName?: string, phone?: string, options?: LoginOptions) => Promise<{ emailSent: boolean }>
  logout: () => Promise<void>
  refreshToken: () => Promise<boolean>
  isAdmin: boolean
  isOwner: boolean
  isAppAdmin: boolean
  isCandidate: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

/** Unwrap the backend envelope `{ id, body, status, message }`. */
async function readJson(response: Response): Promise<{ body: any; message?: string }> {
  const json = await response.json().catch(() => ({}))
  const body = json?.body ?? json
  return { body, message: json?.body?.message || json?.message }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const router = useRouter()

  // Bootstrap session on mount: the refresh endpoint reads the httpOnly refresh
  // cookie and (if valid) rotates it + issues a fresh access cookie, returning
  // the current user/profile. A 401 simply means "not logged in".
  useEffect(() => {
    checkSession()
  }, [])

  // Periodically refresh before the access token expires.
  useEffect(() => {
    if (!session) return

    const checkExpiration = async () => {
      const now = Date.now() / 1000
      const expiresAt = session.expires_at
      if (!expiresAt) return

      if (expiresAt < now) {
        const refreshed = await refreshToken()
        if (!refreshed) logout()
      } else if (expiresAt - now < 300) {
        // Refresh proactively when under 5 minutes remain.
        await refreshToken()
      }
    }

    checkExpiration()
    const interval = setInterval(checkExpiration, 60000)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session])

  const checkSession = async () => {
    try {
      const response = await fetch("/api/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      })
      if (response.ok) {
        const { body } = await readJson(response)
        setSession(body?.profile ? body : null)
      } else {
        setSession(null)
      }
    } catch (error) {
      console.error("Failed to check session:", error)
      setSession(null)
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (email: string, password: string, options: LoginOptions = {}) => {
    const response = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    })

    const { body, message } = await readJson(response)
    if (!response.ok) {
      throw Object.assign(new Error(message || "Login failed"), { code: body?.code })
    }

    setSession(body)

    const profile = body?.profile
    const platformRole = body?.platform_role ?? null
    let assignments: LoginAppAssignment[] = []
    if (!platformRole && profile?.user_type !== "candidate") {
      try {
        const { apiClient } = await import("@/lib/api-client")
        const result = await apiClient.getUserApps({ user_id: body?.user?.id })
        assignments = Array.isArray(result.user_apps) ? result.user_apps : []
      } catch (error) {
        console.error("Failed to fetch user apps for redirect:", error)
        throw new Error("No pudimos verificar tu acceso a las aplicaciones. Intenta iniciar sesión de nuevo.")
      }
    }

    const destination = resolveLoginDestination({ profile, platformRole, assignments, ...options })
    if (destination === "/") {
      throw new Error("Tu cuenta no tiene acceso activo a esta aplicación. Contacta al administrador de tu organización.")
    }
    router.push(destination)
  }

  const signup = async (
    email: string,
    password: string,
    fullName?: string,
    phone?: string,
    options: LoginOptions = {},
  ) => {
    const response = await fetch("/api/auth/signup", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, full_name: fullName, phone, candidate_next: resolveCandidateNext(options.candidateNext) }),
    })

    const { body, message } = await readJson(response)
    if (!response.ok) {
      throw new Error(message || "Signup failed")
    }

    return { emailSent: body?.email_sent === true }
  }

  const refreshToken = async (): Promise<boolean> => {
    try {
      const response = await fetch("/api/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      })
      if (!response.ok) return false

      const { body } = await readJson(response)
      if (body?.profile) {
        setSession(body)
        return true
      }
      return false
    } catch (error) {
      console.error("Token refresh error:", error)
      return false
    }
  }

  const logout = async () => {
    try {
      await fetch("/api/auth/logout", { method: "DELETE" })
    } catch (error) {
      console.error("Logout error:", error)
    } finally {
      setSession(null)
      router.push("/")
    }
  }

  const isOwner = session?.platform_role === 'owner'
  const isAdmin = session?.platform_role === 'owner' || session?.platform_role === 'super_admin'
  const isAppAdmin = session?.profile?.user_type === "app_admin"
  const isCandidate = session?.profile?.user_type === "candidate"

  return (
    <AuthContext.Provider value={{ session, isLoading, login, signup, logout, refreshToken, isAdmin, isOwner, isAppAdmin, isCandidate }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
